import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Switch, FormControlLabel, makeStyles } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import isEqual from 'lodash/isEqual'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import SydSelect from '../../commonDesign/SydSelect'
import { DEFAULT_PREFERENCES, mergePreferences } from './lib/preferencesStorage'

// `value: 'ellipse'` kept as the stored token so existing localStorage
// entries continue to load; the user-facing label is "Ellipsis".
const HEADER_MODE_OPTIONS = [
  { label: 'Wrap', value: 'wrap' },
  { label: 'Ellipsis', value: 'ellipse' }
]

const DENSITY_OPTIONS = [
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const NEGATIVE_FORMAT_OPTIONS = [
  { label: 'Parentheses — (1,234.56)', value: 'parens' },
  { label: 'Minus sign — -1,234.56', value: 'minus' }
]

const DECIMAL_OPTIONS = [0, 1, 2, 3, 4, 5, 6].map(n => ({ label: String(n), value: n }))

// Walk DEFAULT_PREFERENCES once at module load to collect every leaf-field
// path. Adding a new preference field automatically extends "Reset to
// defaults" coverage; a hand-maintained list would silently miss new fields.
function collectLeafPaths (obj, prefix = '') {
  return Object.keys(obj).flatMap(key => {
    const val = obj[key]
    const path = prefix ? `${prefix}.${key}` : key
    return val && typeof val === 'object' && !Array.isArray(val)
      ? collectLeafPaths(val, path)
      : [path]
  })
}

// Leaf-field paths used when "Reset to defaults" puts the form back to the
// shipped baseline. We use `setValue` per path (not `form.reset`) so the
// existing Controller-bound inputs re-render in place. The Apply button's
// enabled state is computed manually via `isEqual(draft, mergePreferences(prefs))`
// (see below), so reset always produces a state that's "dirty" relative to
// the committed prefs -- the user must press Apply to commit the reset.
const LEAF_PATHS = collectLeafPaths(DEFAULT_PREFERENCES)

function getAtPath (obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    minWidth: 460
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    minHeight: 32
  },
  rowLabel: {
    fontSize: 13,
    color: 'inherit'
  },
  rowControl: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  decimalsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingLeft: 12,
    borderLeft: '2px solid var(--color-neutral-200, #eaeaea)'
  },
  decimalsHint: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    lineHeight: 1.4
  },
  switchRow: {
    margin: 0,
    '& .MuiFormControlLabel-label': { fontSize: 13 }
  },
  // SydModalActions sits flush against the body content. Add breathing room
  // above (separates from the last decimals row) and below (so the buttons
  // don't visually hug the form's bottom edge).
  actions: {
    marginTop: 24,
    marginBottom: 8
  },
  resetSlot: {
    marginRight: 'auto'
  }
}))

function Row ({ label, control, classes }) {
  return (
    <div className={classes.row}>
      <span className={classes.rowLabel}>{label}</span>
      <span className={classes.rowControl}>{control}</span>
    </div>
  )
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  control: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired
}

function SelectField ({ name, control, options, minWidth = '160px' }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SydSelect
          options={options}
          value={field.value}
          onChange={(e) => field.onChange(e.target.value)}
          size='sm'
          minWidth={minWidth}
        />
      )}
    />
  )
}

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  minWidth: PropTypes.string
}

function NumberSelectField ({ name, control, options, minWidth = '80px' }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SydSelect
          options={options}
          value={field.value}
          onChange={(e) => field.onChange(Number(e.target.value))}
          size='sm'
          minWidth={minWidth}
        />
      )}
    />
  )
}

NumberSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  minWidth: PropTypes.string
}

function SwitchField ({ name, control, classes, ariaLabel, invertedDefault = false }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          className={classes.switchRow}
          control={(
            <Switch
              checked={invertedDefault ? field.value !== false : !!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              size='small'
              color='primary'
              inputProps={{ 'aria-label': ariaLabel }}
            />
          )}
          // The visible row label is rendered separately by <Row>; pass an
          // empty FormControlLabel label and an explicit aria-label on the
          // input so the switch still has an accessible name.
          label=''
        />
      )}
    />
  )
}

SwitchField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  // For booleans whose default is `true`, treat undefined/missing as on.
  invertedDefault: PropTypes.bool
}

export default function PreferencesDialog ({ open, onClose, prefs, setPrefs }) {
  const classes = useStyles()

  // Hold a draft via react-hook-form so the underlying grid doesn't re-render
  // on every keystroke. Apply commits the draft via setPrefs; Cancel discards.
  const form = useForm({
    defaultValues: mergePreferences(prefs),
    mode: 'onChange'
  })
  const { control, watch, setValue, handleSubmit, reset } = form

  // Resync the draft to the latest committed prefs only on the open
  // transition (false -> true), so a previous Cancel doesn't strand the
  // editor in a stale state. Gating on the transition (rather than just
  // `open`) protects against future code paths that update `prefs` while
  // the dialog is open (e.g. external sync) -- those updates would otherwise
  // wipe the user's in-flight draft.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) reset(mergePreferences(prefs))
    wasOpenRef.current = open
  }, [open, prefs, reset])

  // Use a manual dirty check (current values vs committed prefs) rather than
  // form.formState.isDirty so that "Reset to defaults" produces a state that's
  // dirty relative to the committed prefs, not relative to the form's last
  // reset baseline.
  const draft = watch()
  const isDirty = useMemo(
    () => !isEqual(draft, mergePreferences(prefs)),
    [draft, prefs]
  )

  const handleApply = handleSubmit((values) => {
    setPrefs(values)
    onClose()
  })

  const handleResetToDefaults = useCallback(() => {
    LEAF_PATHS.forEach((path) => {
      setValue(path, getAtPath(DEFAULT_PREFERENCES, path), { shouldDirty: true })
    })
  }, [setValue])

  return (
    <SydModal open={open} onClose={onClose} title='Preferences' size='small'>
      <div className={classes.body}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Styling</div>
          <Row
            classes={classes}
            label='Density'
            control={<SelectField name='styling.density' control={control} options={DENSITY_OPTIONS} />}
          />
          <Row
            classes={classes}
            label='Header text'
            control={<SelectField name='styling.headerMode' control={control} options={HEADER_MODE_OPTIONS} />}
          />
          <Row
            classes={classes}
            label='Zebra striping'
            control={<SwitchField name='styling.zebra' control={control} classes={classes} ariaLabel='Zebra striping' />}
          />
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Display</div>
          <Row
            classes={classes}
            label='Use account nicknames'
            control={<SwitchField name='display.useAccountNicknames' control={control} classes={classes} ariaLabel='Use account nicknames' invertedDefault />}
          />
          <Row
            classes={classes}
            label='Color positive / negative values'
            control={<SwitchField name='display.growthColors' control={control} classes={classes} ariaLabel='Color positive / negative values' invertedDefault />}
          />
          <Row
            classes={classes}
            label='Show currency symbol'
            control={<SwitchField name='display.showCurrencySymbol' control={control} classes={classes} ariaLabel='Show currency symbol' invertedDefault />}
          />
          <Row
            classes={classes}
            label='Negative numbers'
            control={<SelectField name='display.negativeFormat' control={control} options={NEGATIVE_FORMAT_OPTIONS} minWidth='220px' />}
          />
          <div className={classes.decimalsGroup}>
            <div className={classes.decimalsHint}>Decimal places by measure</div>
            <Row
              classes={classes}
              label='Currency'
              control={<NumberSelectField name='display.decimals.currency' control={control} options={DECIMAL_OPTIONS} />}
            />
            <Row
              classes={classes}
              label='Returns'
              control={<NumberSelectField name='display.decimals.return' control={control} options={DECIMAL_OPTIONS} />}
            />
            <Row
              classes={classes}
              label='Weights'
              control={<NumberSelectField name='display.decimals.weight' control={control} options={DECIMAL_OPTIONS} />}
            />
            <Row
              classes={classes}
              label='Ratios / multipliers'
              control={<NumberSelectField name='display.decimals.ratio' control={control} options={DECIMAL_OPTIONS} />}
            />
          </div>
        </div>
      </div>

      <SydModalActions className={classes.actions}>
        <span className={classes.resetSlot}>
          <SydButton variant='outline' size='sm' onClick={handleResetToDefaults}>Reset to defaults</SydButton>
        </span>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton variant='primary' size='sm' onClick={handleApply} disabled={!isDirty}>Apply</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

PreferencesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prefs: PropTypes.shape({
    styling: PropTypes.object,
    display: PropTypes.object
  }).isRequired,
  setPrefs: PropTypes.func.isRequired
}
